// 🔒 PROTECTED — PricePAL Compare
// Owner: Garry Yee | Do not modify without explicit written permission
// See /app/PROTECTED_CODE_REGISTRY.md for full rules
/**
 * Feature Gate Component
 * Blocks access to premium features for free users
 * Shows upgrade prompt instead
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';

const UPGRADE_TIERS = [
  { key: 'vip_founder', name: 'VIP Founder', price: '1.00', founderTier: 'VIP_FOUNDER', color: 'border-amber-400 bg-amber-50', badge: 'LIFETIME', badgeColor: 'bg-amber-500', textColor: 'text-amber-600', features: ['All Premium features', 'Lifetime founder rate — £1/mo forever', 'VIP Founder badge'] },
  { key: 'early_founder', name: 'Early Founder', price: '1.00', founderTier: 'EARLY_FOUNDER', color: 'border-emerald-400 bg-emerald-50', badge: 'LIFETIME', badgeColor: 'bg-emerald-500', textColor: 'text-emerald-600', features: ['All Premium features', 'Lifetime founder rate — £1/mo forever', 'Early Founder badge'] },
  { key: 'individual', name: 'Premium', price: '1.99', founderTier: null, color: 'border-blue-200', badge: null, badgeColor: '', textColor: 'text-blue-600', features: ['All premium features', 'Unlimited scans & alerts', 'Cancel anytime'] },
  { key: 'family', name: 'Family', price: '2.99', founderTier: null, color: 'border-purple-400 bg-purple-50', badge: 'FAMILY', badgeColor: 'bg-purple-500', textColor: 'text-purple-600', features: ['Up to 4 family members', 'Shared shopping lists', 'Family savings dashboard'] },
];

function FeatureGateCards({ loading, setLoading, backendUrl }) {
  const [capacity, setCapacity] = useState(null);
  useEffect(() => {
    axios.get(`${backendUrl}/api/founder-capacity/status`).then(r => setCapacity(r.data)).catch(() => {});
  }, [backendUrl]);

  const visibleTiers = UPGRADE_TIERS.filter(t => {
    if (!t.founderTier) return true;
    if (!capacity) return false;
    const tierCap = capacity.tiers?.[t.founderTier];
    return tierCap && !tierCap.is_full;
  });

  const goToCheckout = async (tierKey) => {
    setLoading(tierKey);
    try {
      const res = await axios.post(`${backendUrl}/api/stripe-payments/checkout/create`, {
        tier_id: tierKey, billing_cycle: 'monthly', origin_url: window.location.origin
      }, { withCredentials: true });
      if (res.data?.url || res.data?.checkout_url) window.location.href = res.data.url || res.data.checkout_url;
      else { alert('Could not start checkout.'); setLoading(null); }
    } catch (err) { alert(`Upgrade failed: ${err.response?.data?.detail || err.message}`); setLoading(null); }
  };

  return (
    <div className={`grid grid-cols-1 ${visibleTiers.length > 2 ? 'sm:grid-cols-2' : 'md:grid-cols-2'} gap-3 mb-6`}>
      {visibleTiers.map(t => (
        <button key={t.key} disabled={loading === t.key} onClick={() => goToCheckout(t.key)}
          className={`border-2 ${t.color} rounded-xl p-5 hover:shadow-lg transition cursor-pointer text-left w-full relative`}>
          {t.badge && <div className={`absolute -top-2 -right-2 ${t.badgeColor} text-white text-[10px] font-bold px-2 py-0.5 rounded-full`}>{t.badge}</div>}
          <div className="text-center mb-3">
            <div className="text-lg font-bold text-gray-900">{t.name}</div>
            <div className={`text-2xl font-bold ${t.textColor} mt-1`}>£{t.price}<span className="text-sm text-gray-500">/mo</span></div>
          </div>
          <ul className="text-xs text-gray-600 space-y-1 mb-3">
            {t.features.map((f, i) => <li key={i}>✓ {f}</li>)}
          </ul>
          <div className={`text-center text-sm font-semibold ${t.textColor}`}>
            {loading === t.key ? 'Redirecting...' : `Upgrade to ${t.name}`}
          </div>
        </button>
      ))}
    </div>
  );
}

const FeatureGate = ({ 
  userTier, 
  requiredTier = 'pro',
  featureName,
  onUpgradeClick,
  userData,
  children 
}) => {
  const [loading, setLoading] = useState(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
  
  // LOADING GUARD: Don't show paywall while user data is still loading
  if (!userData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }
  
  // ============================================================
  // TIER ACCESS RULES — Canonical tier system
  // ALPHA_TESTER  → FULL ACCESS, lifetime free (Beta Tester)
  // VIP_FOUNDER   → FULL ACCESS, discounted paid (Beta Tester)
  // EARLY_FOUNDER → FULL ACCESS, discounted paid (Beta Tester)
  // INDIVIDUAL    → FULL ACCESS (paid)
  // FAMILY        → FULL ACCESS + family features (paid)
  // PRO_TRIAL     → FULL ACCESS during 7-day trial
  // FREE          → LIMITED (show upgrade prompt)
  // ============================================================
  
  const FULL_ACCESS_TIERS = [
    'ALPHA_TESTER', 'VIP_FOUNDER', 'EARLY_FOUNDER',
    'INDIVIDUAL', 'FAMILY', 'PRO_TRIAL', 'TRIAL'
  ];
  
  const tier = userData?.subscription_tier;
  
  // Full access: canonical tier match, beta/alpha flags, lifetime, or active trial
  const hasFullAccess = 
    FULL_ACCESS_TIERS.includes(tier) ||
    userData?.is_beta_tester === true ||
    userData?.is_alpha_tester === true ||
    userData?.lifetime_access === true ||
    userData?.trial_active === true;
  
  if (hasFullAccess) {
    return <>{children}</>;
  }
  
  // Detect if user has already used their free trial (don't offer trial again)
  const hasUsedTrial = userData?.trial_used === true || userData?.trial_end_date != null;

  // User doesn't have access - show upgrade prompt
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-8 text-center">
          <div className="text-6xl mb-4">🔒</div>
          <h2 className="text-3xl font-bold mb-2">Premium Feature</h2>
          <p className="text-blue-100 text-lg">
            {featureName || 'This feature'} is available on Pro and Family plans
          </p>
        </div>
        
        {/* Content */}
        <div className="p-8">
          <div className="mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              Why upgrade?
            </h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <span className="text-green-600 text-xl">✓</span>
                <span className="text-gray-700">Compare unlimited products across all retailers</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-green-600 text-xl">✓</span>
                <span className="text-gray-700">Optimize shopping lists for maximum savings</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-green-600 text-xl">✓</span>
                <span className="text-gray-700">Save unlimited shopping lists</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-green-600 text-xl">✓</span>
                <span className="text-gray-700">Access AI-powered Kitchen Assistant</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-green-600 text-xl">✓</span>
                <span className="text-gray-700">No ads, priority support</span>
              </li>
            </ul>
          </div>
          
          {/* Pricing Cards — capacity-aware, direct to Stripe */}
          <FeatureGateCards loading={loading} setLoading={setLoading} backendUrl={backendUrl} />
          
          <p className="text-center text-sm text-gray-500 mt-4">
            Secure checkout powered by Stripe • Cancel anytime
          </p>
        </div>
      </div>
    </div>
  );
};

export default FeatureGate;
